// ===================================================
// branch-rows — from a raw git ref to one readable table row.
//
// WHY: גל, 06.09.2026: «בטבלה שאתה פותח של הענפים ב-vdev preview אני רוצה
// לעשות ממש טבלה מסודרת שאני אבין מה אני רואה, ולא שורות ארוכות של מידע».
// עד היום הרשימה הייתה `<select>` ובכל אופציה שורה אחת מחוברת בנקודות:
// שם, סחיפה, נושא וזמן — קריאה למכונה, לא לאדם.
//
// כל מה שכאן הוא **גזירה טהורה** מן המידע שהמתג כבר מחזיר, בלי קריאת רשת
// ובלי מצב. זה מה שמאפשר לבדוק אותו, וזה מה שמונע כלל עסקי שמתחבא בתוך JSX.
// ===================================================

/** ענף כפי שהמתג ב-VPS מדווח עליו. רק `name` מובטח. */
export interface BranchInfo {
  name: string;
  sha?: string;
  date?: string;      // committerdate — מתי נערך לאחרונה
  author?: string;
  ahead?: number;
  behind?: number;
  subject?: string;
  /** מתי הענף נפתח. המתג עדיין אינו מחזיר את זה — ראה openedAt למטה. */
  createdAt?: string;
}

export type BranchState = "merged" | "active" | "idle" | "abandoned";

export interface BranchRow {
  name: string;
  /** השם הקצר לתצוגה — בלי קידומת ובלי תאריך */
  short: string;
  /** האפליקציה שהענף נוגע בה, מתוך המקטע האמצעי בשם. */
  app: string;
  /** סוג העבודה — feat · fix · chore … */
  kind: string;
  state: BranchState;
  stateLabel: string;
  /** הסטטוס מ-PM כשיש משימה שמצביעה על הענף, אחרת הסטטוס הנגזר מגיט. */
  pmStatus: string;
  subject: string;
  editedAt: string;
  editedAgo: string;
  openedAt: string;
  openedAgo: string;
  author: string;
  drift: string;
  ahead: number;
  behind: number;
  sha: string;
}

/** קידומות שהן עבודה אמיתית — אלה שגם שער השמות בודק. */
export const WORK_KINDS = ["feat", "fix", "chore", "docs", "spec", "refactor", "perf", "test"];

/**
 * הדלי לענף בלי מקטע אפליקציה.
 *
 * גל שאל במפורש: «ומה קורה אם אין App אז זה יופיע ריק?» — לא. תא ריק נקרא
 * כמו תקלה בטבלה ואיש אינו מטפל בו, ואילו דלי בעל שם ניתן לסינון בלחיצה
 * אחת ולכן מזמין ניקוי. שלושים ושלושה הענפים החיים היום כולם ייפלו לכאן.
 */
export const NO_APP = "ללא שיוך";

export function appOf(name: string): string {
  const parts = name.split("/");
  if (parts.length >= 3 && WORK_KINDS.includes(parts[0])) return parts[1];
  return NO_APP;
}

/**
 * השם הקצר של הענף — מה שנשאר אחרי הקידומת והתאריך.
 * `feat/vdev/branch-table-20260906` → `branch-table`.
 * הסוג והאפליקציה כבר יושבים בעמודות שלהם, והתאריך בעמודת הזמן, ולכן
 * חזרתם בתוך השם היא שכפול שמאריך את השורה בלי להוסיף מידע.
 */
export function shortOf(name: string): string {
  const tail = name.split("/").pop() ?? name;
  return tail.replace(/-20\d{6}$/, "") || tail;
}

export function kindOf(name: string): string {
  const first = name.split("/")[0];
  return name.includes("/") ? first : "—";
}

/** מנרמל תאריך של גיט (ISO עם היסט, לפעמים עם רווח) למספר מילישניות. */
export function parseGitDate(iso?: string): number | null {
  if (!iso) return null;
  const t = new Date(iso.replace(" ", "T").replace(/ ([+-]\d{4})$/, "$1")).getTime();
  return Number.isFinite(t) ? t : null;
}

/** «לפני 3 ימים» — אותה שפה שכבר קיימת בסרגל, כדי שלא יהיו שתי שפות זמן. */
export function ago(iso?: string, now = Date.now()): string {
  const t = parseGitDate(iso);
  if (t === null) return "";
  const days = Math.round((now - t) / 86400000);
  if (days <= 0) return "היום";
  if (days === 1) return "אתמול";
  if (days < 30) return `לפני ${days} ימים`;
  const months = Math.round(days / 30);
  if (months < 12) return `לפני ${months} חודשים`;
  return `לפני ${Math.round(months / 12)} שנים`;
}

/** «06.09.2026 · 16:52» — גל ביקש תאריך **ושעה**, לא רק «לפני כמה זמן». */
export function stamp(iso?: string): string {
  const t = parseGitDate(iso);
  if (t === null) return "";
  const d = new Date(t);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} · ${p(d.getHours())}:${p(d.getMinutes())}`;
}

const STATE_LABEL: Record<BranchState, string> = {
  merged: "מוזג",
  active: "בעבודה",
  idle: "עומד",
  abandoned: "נטוש",
};

/**
 * מצב הענף, נגזר ממה שנמדד ולא מוצהר.
 *
 * `ahead === 0` פירושו שכל קומיט בענף כבר נמצא בראשי — הענף גמור, גם אם איש
 * לא מחק אותו. זו בדיוק הקבוצה שמנתה 165 מתוך 198 במדידה של 06.09.2026.
 */
export function stateOf(b: BranchInfo, now = Date.now()): BranchState {
  if ((b.ahead ?? 0) === 0) return "merged";
  const t = parseGitDate(b.date);
  if (t === null) return "idle";
  const days = (now - t) / 86400000;
  if (days <= 14) return "active";
  if (days <= 60) return "idle";
  return "abandoned";
}

/**
 * שורה אחת בטבלה.
 *
 * `pmByBranch` הוא מה שכבר קיים ב-PM: העמודה `tasks.git_branch` נמדדה
 * 06.09.2026 ובה 22 משימות מתוך 101, על 15 ענפים שונים. כשיש התאמה,
 * הסטטוס האמיתי של המשימה גובר על הניחוש מגיט — כי הוא נכתב בידי אדם.
 */
export function toRow(
  b: BranchInfo,
  pmByBranch: Record<string, string> = {},
  now = Date.now(),
): BranchRow {
  const state = stateOf(b, now);
  const ahead = b.ahead ?? 0;
  const behind = b.behind ?? 0;
  return {
    name: b.name,
    short: shortOf(b.name),
    app: appOf(b.name),
    kind: kindOf(b.name),
    state,
    stateLabel: STATE_LABEL[state],
    pmStatus: pmByBranch[b.name] ?? STATE_LABEL[state],
    subject: b.subject ?? "",
    editedAt: stamp(b.date),
    editedAgo: ago(b.date, now),
    openedAt: stamp(b.createdAt),
    openedAgo: ago(b.createdAt, now),
    author: b.author ?? "",
    drift: `${ahead}↑ ${behind}↓`,
    ahead,
    behind,
    sha: b.sha ?? "",
  };
}

export function toRows(
  list: BranchInfo[],
  pmByBranch: Record<string, string> = {},
  now = Date.now(),
): BranchRow[] {
  return list.map((b) => toRow(b, pmByBranch, now));
}
